/**
 * Plans API Service
 * 
 * All subscription plans, features, and boosters API endpoints
 * following the API v2.0 specification.
 */

import { get, post, put, del } from '../../utils/httpClient';
import logger from '../../utils/logger';

// ============ PLANS ============

/**
 * Get all subscription plans
 */
export const fetchAllPlans = async () => {
  try {
    const response = await get('/plans/list');
    // Handle both direct array response and { data: [...] } structure
    if (Array.isArray(response)) {
      return response;
    }
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all plans', error);
    throw error;
  }
};

/**
 * Get a single plan by ID
 */
export const fetchPlanById = async (id) => {
  try {
    if (!id) {
      throw new Error('Plan ID is required');
    }
    const response = await post('/plans/list', { id });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch plan by ID', error, { id });
    throw error;
  }
};

// ============ FEATURES ============

/**
 * Get all plan features
 */
export const fetchAllFeatures = async () => {
  try {
    const response = await get('/plans/features/list');
    // Handle both direct array response and { data: [...] } structure
    if (Array.isArray(response)) {
      return response;
    }
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all features', error);
    throw error;
  }
};

/**
 * Get a single feature by ID
 */
export const fetchFeatureById = async (featureId) => {
  try {
    if (!featureId) {
      throw new Error('Feature ID is required');
    }
    // Note: API still uses { data: { feature_id } } format for this endpoint
    const response = await post('/plans/features/list', {
      data: { feature_id: featureId },
    });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch feature by ID', error, { featureId });
    throw error;
  }
};

// ============ BOOSTERS ============

/**
 * Get all plan boosters
 */
export const fetchAllBoosters = async () => {
  try {
    const response = await get('/plans/boosters/list');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all boosters', error);
    throw error;
  }
};

/**
 * Get a single booster by ID
 */
export const fetchBoosterById = async (boosterId) => {
  try {
    if (!boosterId) {
      throw new Error('Booster ID is required');
    }
    // Note: API still uses { data: boosterId } format for this endpoint
    const response = await post('/plans/boosters/list', {
      data: boosterId,
    });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch booster by ID', error, { boosterId });
    throw error;
  }
};

